import {
    ChannelTile,
    ChevronLeft,
    Chips,
    CollectionTile,
    ProductCard,
    SectionTitle,
    SideTag,
    StoreTile,
} from '@kaman/uikit';
import type {CSSProperties, MouseEvent as ReactMouseEvent} from 'react';
import {useRef} from 'react';
import {useQuery} from 'react-query';
import {useNavigate, type NavigateFunction} from 'react-router-dom';
import {
    IGetStoreProductsResponse,
    IGetStoresSections,
    SectionStyle,
} from '../../../models/store.model';
import {
    GetSectionItems,
    STALE_10_MIN,
    STORE_QUERY_KEYS,
} from '../../../services/store.service';
import {removeEmTags} from '../../../utility/removeEmTags';
import WideStore from './wideStore';
import {SECTION_SPACING} from './sectionSpacing';

interface SectionRendererProps {
    section: IGetStoresSections;
    onNavigate: (route?: string | null) => void;
    storeChannel?: 'online' | 'offline';
    productsLayout?: 'carousel' | 'grid';
}

// Horizontal tile gaps are independent of the shared vertical spacing.
const SECTION_HORIZONTAL_SPACING: Partial<Record<SectionStyle, string>> = {
    ProductCategories: '0.5rem',
    SmallStores: '0.25rem',
    Tags: '0.5rem',
};
const DEFAULT_HORIZONTAL_SPACING = '0.75rem';

const PRODUCT_CARD_HEIGHT = 260;

const SECTION_TILE_HEIGHT: Record<SectionStyle, number> = {
    ProductCategories: 96,
    SmallStores: 145,
    MediumStores: 130,
    Products: PRODUCT_CARD_HEIGHT + 30,
    ProductsWithVerticalCaption: PRODUCT_CARD_HEIGHT + 30,
    WideStores: 130,
    Banner: 0,
    Tags: 0,
};

const renderTile = (
    tile: IGetStoreProductsResponse,
    style: SectionStyle,
    key: string,
    onNavigate: (route?: string | null) => void,
    storeChannel: 'online' | 'offline' | undefined,
    productsLayout: 'carousel' | 'grid' = 'carousel',
    navigate?: NavigateFunction
) => {
    const getCategoryId = (route?: string | null): number | null => {
        if (!route) return null;
        const match = route.match(/categoryId=\$?\{?(\d+)\}?/);
        return match ? Number(match[1]) : null;
    };
    switch (style) {
        case 'ProductCategories':
            return (
                <CollectionTile
                    key={key}
                    backgroundColor="#F3F5F5"
                    height="96px"
                    width="64px"
                    className="[&>div:nth-last-child(2)]:h-[60px] [&>div:nth-last-child(2)]:flex-none [&>div:nth-last-child(2)]:px-0 [&>div:nth-last-child(2)]:pt-3 [&>div:nth-last-child(2)]:pb-[5px] [&>div:last-child]:flex-1 [&>div:last-child]:pb-2 [&>div:last-child>*]:text-[0.625rem] [&>div:last-child>*]:leading-tight"
                    icon={
                        <img
                            src={tile.imageUrl}
                            alt=""
                            draggable={false}
                            className="h-[43px] w-[47px] shrink-0 object-contain"
                        />
                    }
                    title={tile.title}
                    onClick={() => {
                        const categoryId = getCategoryId(tile.route);
                        if (tile.target === 'tag') {
                            if (!tile.key) return;
                            const channel = storeChannel ?? 'online';
                            navigate?.(
                                `/home/${channel}-search?${new URLSearchParams({tag: tile.key})}`
                            );
                        } else if (tile.target === 'category') {
                            if (categoryId === null) return;
                            navigate?.(
                                `/home/${storeChannel ?? 'online'}-search?categoryId=${categoryId}`
                            );
                        }
                    }}
                />
            );

        case 'SmallStores':
            return (
                <ChannelTile
                    key={key}
                    imageSrc={tile.logoUrl ?? tile.imageUrl}
                    title={tile.title}
                    onClick={() => {
                        onNavigate(tile.route);
                        // onNavigate(`/home/store-detail?storeId=${2}`);
                    }}
                    // inPerson
                    // online
                />
            );

        case 'WideStores':
            return <WideStore key={key} store={tile} onNavigate={onNavigate} />;

        case 'Tags': {
            const tag = tile.key;
            return (
                <Chips
                    key={key}
                    textColor="#1c1c1c"
                    className="border-[#1c1c1c] px-8 py-0 h-6"
                    onClick={() => {
                        if (!tag) return;
                        const channel = storeChannel ?? 'online';
                        navigate?.(
                            `/home/${channel}-search?${new URLSearchParams({tag})}`
                        );
                    }}
                >
                    {tile.title}
                </Chips>
            );
        }

        case 'MediumStores':
            return (
                <StoreTile
                    key={key}
                    className="[&>div:first-child_img]:!max-w-[120px] [&>div:first-child_img]:!max-h-[72px]"
                    imageSrc={tile.imageUrl}
                    title={tile.title}
                    badge={tile.badge ?? undefined}
                    imageBackgroundColor={tile.backgroundColor ?? '#D4F8FD'}
                    onClick={() => {
                        onNavigate(tile.route);
                    }}
                />
            );

        case 'Products':
        case 'ProductsWithVerticalCaption': {
            const hasDiscount =
                !!tile.discountedPercent && tile.discountedPercent > 0;
            return (
                <ProductCard
                    key={key}
                    titleMaxLength={24}
                    style={
                        productsLayout === 'grid'
                            ? {width: '100%', height: PRODUCT_CARD_HEIGHT}
                            : {
                                  width: '144px',
                                  height: PRODUCT_CARD_HEIGHT,
                                  flexShrink: 0,
                              }
                    }
                    title={removeEmTags(tile.title)}
                    subtitle={tile.description1 ?? ''}
                    storeTitle={tile.description2 ?? ''}
                    imageSrc={tile.imageUrl}
                    logoUrl={tile.logoUrl ?? ''}
                    badgeText={tile.badge ?? ''}
                    currency={tile.currency ?? ''}
                    price={tile.price?.toLocaleString('fa') ?? ''}
                    originalPrice={
                        hasDiscount
                            ? (
                                  (tile.price ?? 0) +
                                  (tile.discountedPrice ?? 0)
                              ).toLocaleString('fa')
                            : ''
                    }
                    discountPercent={
                        hasDiscount
                            ? (tile.discountedPercent ?? undefined)
                            : undefined
                    }
                    onClick={() =>
                        navigate
                            ? navigate('/home/product-preview', {state: tile})
                            : onNavigate(tile.route)
                    }
                />
            );
        }

        case 'Banner':
            return (
                <section data-section="store-advertisement">
                    <button
                        type="button"
                        className="block w-full overflow-hidden rounded-lg"
                        onClick={() => onNavigate(tile.route)}
                        disabled={!tile.route}
                        aria-label={`مشاهده تبلیغ ${tile.title}`}
                    >
                        <img
                            src={tile.imageUrl}
                            alt={tile.title}
                            className="block h-auto w-full object-cover"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    </button>
                </section>
            );

        default: {
            const _exhaustiveCheck: never = style;
            return null;
        }
    }
};

export const SectionRenderer = ({
    section,
    onNavigate,
    storeChannel,
    productsLayout = 'carousel',
}: SectionRendererProps) => {
    const navigate = useNavigate();
    const hasLazyItemsKey = Boolean(section.sectionItemsKey?.trim());
    const isProductsSection =
        section.sectionStyle === 'Products' ||
        section.sectionStyle === 'ProductsWithVerticalCaption';
    const isProductsGrid = productsLayout === 'grid' && isProductsSection;
    const isChannelsSection = section.sectionStyle === 'SmallStores';

    const {data: lazyItems, isFetching: lazyItemsLoading} = useQuery(
        hasLazyItemsKey
            ? STORE_QUERY_KEYS.sectionItems(section.sectionItemsKey!.trim())
            : ['__NO_SECTION_ITEMS_KEY__', section.mainTitle ?? ''],
        hasLazyItemsKey
            ? () =>
                  GetSectionItems({
                      sectionItemsKey: section.sectionItemsKey!.trim(),
                  })
            : () =>
                  Promise.resolve<IGetStoreProductsResponse[] | undefined>(
                      undefined
                  ),
        {
            staleTime: STALE_10_MIN,
            enabled: hasLazyItemsKey,
            refetchOnWindowFocus: false,
            retry: 1,
        }
    );

    const sectionItems: IGetStoreProductsResponse[] | undefined =
        hasLazyItemsKey ? lazyItems : section.sectionItems;
    const channelColumns = Math.max(
        1,
        Math.ceil((sectionItems?.length ?? 0) / 2)
    );

    const dragStateRef = useRef<{
        isDown: boolean;
        startX: number;
        startY: number;
        startScrollLeft: number;
        moved: boolean;
    }>({isDown: false, startX: 0, startY: 0, startScrollLeft: 0, moved: false});
    const blockClickRef = useRef(false);
    const DRAG_THRESHOLD_PX = 6;

    const suppressNextClick = () => {
        blockClickRef.current = true;
        setTimeout(() => {
            blockClickRef.current = false;
        }, 0);
    };

    const handleItemClickCapture = (e: ReactMouseEvent) => {
        if (blockClickRef.current) {
            e.stopPropagation();
            e.preventDefault();
            blockClickRef.current = false;
        }
    };

    const handleMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
        if (e.button !== 0) return;
        const el = e.currentTarget;
        dragStateRef.current = {
            isDown: true,
            startX: e.clientX,
            startY: e.clientY,
            startScrollLeft: el.scrollLeft,
            moved: false,
        };
        (e.currentTarget as HTMLElement).style.userSelect = 'none';
    };

    const handleMouseLeave = (e: ReactMouseEvent<HTMLDivElement>) => {
        const st = dragStateRef.current;
        if (st.isDown && st.moved) {
            suppressNextClick();
        }
        st.isDown = false;
        (e.currentTarget as HTMLElement).style.userSelect = '';
    };

    const handleMouseUp = (e: ReactMouseEvent<HTMLDivElement>) => {
        const st = dragStateRef.current;
        if (st.isDown && st.moved) {
            e.preventDefault();
            suppressNextClick();
        }
        st.isDown = false;
        (e.currentTarget as HTMLElement).style.userSelect = '';
    };

    const handleMouseMove = (e: ReactMouseEvent<HTMLDivElement>) => {
        const st = dragStateRef.current;
        if (!st.isDown) return;
        const el = e.currentTarget;
        const dx = e.clientX - st.startX;
        const dy = e.clientY - st.startY;
        const walk = dx * 1.2;
        if (!st.moved) {
            if (
                Math.abs(dx) >= DRAG_THRESHOLD_PX &&
                Math.abs(dx) >= Math.abs(dy)
            ) {
                st.moved = true;
            }
        }
        if (st.moved) {
            e.preventDefault();
            el.scrollLeft = st.startScrollLeft - walk;
        }
    };

    const isBannerSection = section.sectionStyle === 'Banner';
    const sideTagHeight = SECTION_TILE_HEIGHT[section.sectionStyle];

    return (
        <div
            className="w-full min-w-0 flex flex-col gap-[var(--section-spacing)] overflow-x-hidden"
            style={
                {
                    '--section-spacing': SECTION_SPACING,
                    '--section-horizontal-spacing':
                        SECTION_HORIZONTAL_SPACING[section.sectionStyle] ??
                        DEFAULT_HORIZONTAL_SPACING,
                } as CSSProperties
            }
        >
            <style>{`
                .sr-hide-scroll::-webkit-scrollbar {
                    display: none;
                }
                .sr-hide-scroll {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                @media (pointer: fine) {
                    .sr-hide-scroll {
                        cursor: grab;
                    }
                    .sr-hide-scroll:active {
                        cursor: grabbing;
                    }
                }
            `}</style>
            {section.mainTitle && section.sectionStyle !== 'WideStores' && (
                <div className="w-full">
                    <SectionTitle
                        leftSide={
                            section.showMore ? <ChevronLeft size={20} /> : null
                        }
                        onClick={() => {
                            onNavigate(section.moreRoute);
                        }}
                        rightSide={section.mainTitle}
                        showDivider={false}
                        className="!px-0 [&>div]:!p-0 [&>div]:!min-w-0 [&_h5]:text-[0.9rem]"
                    />
                </div>
            )}
            {section.backgroundImageUrl && (
                <div className="w-full overflow-hidden rounded-lg">
                    <img
                        src={section.backgroundImageUrl}
                        alt={section.mainTitle ?? ''}
                        className="w-full h-auto object-cover"
                    />
                </div>
            )}
            {section.sectionStyle === 'WideStores' ? (
                <WideStore
                    section={{...section, sectionItems: sectionItems ?? []}}
                    onNavigate={onNavigate}
                />
            ) : section.sectionStyle === 'Tags' ? (
                <div
                    dir="rtl"
                    className="flex w-full flex-wrap gap-y-[var(--section-spacing)] gap-x-[var(--section-horizontal-spacing)]"
                >
                    {sectionItems?.map((tile, idx) =>
                        renderTile(
                            tile,
                            section.sectionStyle,
                            `${section.mainTitle ?? 'section'}-${idx}`,
                            onNavigate,
                            storeChannel,
                            productsLayout,
                            navigate
                        )
                    )}
                </div>
            ) : isBannerSection ? (
                <div
                    dir="rtl"
                    className={`w-full min-w-0 flex flex-col gap-[var(--section-spacing)] ${
                        section.backgroundImageUrl ? '-mt-80 z-10' : ''
                    }`}
                >
                    {sectionItems?.map((tile, idx) => (
                        <div
                            key={idx}
                            draggable={false}
                            className="w-full min-w-0 select-none"
                        >
                            {renderTile(
                                tile,
                                section.sectionStyle,
                                `${section.mainTitle ?? 'section'}-${idx}`,
                                onNavigate,
                                storeChannel,
                                productsLayout,
                                navigate
                            )}
                        </div>
                    ))}
                </div>
            ) : isProductsGrid ? (
                <div
                    dir="rtl"
                    className={`w-full min-w-0 grid grid-cols-2 gap-y-[var(--section-spacing)] gap-x-[var(--section-horizontal-spacing)] ${
                        section.backgroundImageUrl ? '-mt-80 z-10' : ''
                    }`}
                >
                    {sectionItems?.map((tile, idx) => (
                        <div
                            key={idx}
                            draggable={false}
                            className="min-w-0 w-full select-none"
                        >
                            {renderTile(
                                tile,
                                section.sectionStyle,
                                `${section.mainTitle ?? 'section'}-${idx}`,
                                onNavigate,
                                storeChannel,
                                productsLayout,
                                navigate
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div
                    dir="rtl"
                    className={`relative w-full min-w-0 flex flex-row gap-x-[var(--section-horizontal-spacing)] ${isProductsSection ? 'items-center' : 'items-start'} ${
                        section.backgroundImageUrl ? '-mt-80 z-10' : ''
                    }`}
                >
                    {section.caption && (
                        <div className="flex-shrink-0">
                            <SideTag
                                backgroundColor={
                                    section.captionBackgroundColor || '#e7e2d8'
                                }
                                height={sideTagHeight}
                                placement="left"
                                text={section.caption}
                                textColor={section.captionColor || '#7a4a00'}
                                width={40}
                            />
                        </div>
                    )}
                    <div
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        onMouseMove={handleMouseMove}
                        onDragStart={(e) => e.preventDefault()}
                        style={{
                            overscrollBehaviorX: 'contain',
                            WebkitOverflowScrolling: 'touch',
                            // Native touch scrolling preserves mobile momentum and page panning.
                            touchAction: 'auto',
                            rowGap: isChannelsSection
                                ? '0.5rem'
                                : SECTION_SPACING,
                        }}
                        className={`sr-hide-scroll min-w-0 flex-1 items-start gap-y-[var(--section-spacing)] gap-x-[var(--section-horizontal-spacing)] overflow-x-auto overflow-y-hidden ${
                            isChannelsSection
                                ? 'grid grid-rows-2 auto-cols-max'
                                : 'flex flex-row'
                        }`}
                    >
                        {sectionItems?.map((tile, idx) => (
                            <div
                                key={idx}
                                draggable={false}
                                onClickCapture={handleItemClickCapture}
                                style={
                                    isChannelsSection
                                        ? {
                                              gridRow:
                                                  Math.floor(
                                                      idx / channelColumns
                                                  ) + 1,
                                              gridColumn:
                                                  (idx % channelColumns) + 1,
                                          }
                                        : undefined
                                }
                                className={`flex-shrink-0 select-none ${
                                    section.sectionStyle === 'WideStores'
                                        ? 'w-full'
                                        : ''
                                }`}
                            >
                                {renderTile(
                                    tile,
                                    section.sectionStyle,
                                    `${section.mainTitle ?? 'section'}-${idx}`,
                                    onNavigate,
                                    storeChannel,
                                    productsLayout,
                                    navigate
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
